import React, { useContext } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import styled from 'styled-components';
import { TimeRange } from '@kentik/ui-shared/query/query.types';
import { Box, Button, Flex } from 'core/components';
import TimeNavigationControl from 'app/views/boards/TimeNavigationControl';
import $boards from 'app/stores/boards/$boards';
import BoardModel from 'app/stores/boards/BoardModel';
import BoardPanelModel from 'app/stores/boards/BoardPanelModel';
import BoardVariables from 'app/views/boards/BoardVariables';
import { Variable } from '@kentik/ui-shared/boards/boards';
import { useBoardVariableUrlSync } from 'app/views/boards/useBoardVariableUrlSync';
import { useBoardTimeUrlSync } from 'app/views/boards/useBoardTimeUrlSync';
import { TbFilter } from 'react-icons/tb';
import UdeDebugButton from './debug/UdeDebugButton';

interface Props {
  board: BoardModel;
  panel?: BoardPanelModel;
  onTimeChange: (time: TimeRange) => void;
  onTimezoneChange?: (timezone: string) => void;
  onLiveUpdateChange: (updateFrequency: number) => void;
  /** Opens the board settings drawer at the Filters section. */
  onOpenFilters?: () => void;
  hasMeasurement?: boolean;
  /** When true, the toolbar becomes sticky and shows a shadow while collapsed. */
  sticky?: boolean;
  /** When true (requires `sticky`), renders the subtle shadow indicating sticky state. */
  collapsed?: boolean;
  /** Optional trailing slot (e.g. time range) rendered at the right edge. */
  rightSlot?: React.ReactNode;
}

const StickyWrapper = styled(Box)<{ $collapsed?: boolean }>`
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 8px 24px 8px 12px;
  background: ${({ theme }) => theme.colors.decks.background};
  transition:
    top 200ms ease,
    box-shadow 150ms ease;
  ${({ $collapsed }) =>
    $collapsed &&
    `
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06), 0 6px 12px -8px rgba(0, 0, 0, 0.12);
  `}
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

function UdeToolBar({
  board,
  panel,
  onTimeChange,
  onTimezoneChange,
  onLiveUpdateChange,
  onOpenFilters,
  hasMeasurement = true,
  sticky,
  collapsed,
  rightSlot
}: Props) {
  const { $auth } = useContext(MobXProviderContext);
  const timeRange = panel?.queryModel?.query?.time ?? board.get('config').time ?? { lookback: 3600, start: 0, end: 0 };
  const timezone =
    panel?.queryModel?.settings.chart.xAxis?.timezone ?? board.get('config').timezone ?? $auth.userTimezone;
  const autoRefresh = panel?.queryModel?.query?.updateFrequency ?? board.get('config').updateFrequency ?? 0;
  const isLoading = panel?.queryModel?.isLoading ?? board.panelCollection.models.some((p) => p.queryModel?.isLoading);
  const hasRunQuery = panel?.queryModel?.hasRun ?? board.panelCollection.models.some((p) => p.queryModel?.hasRun);

  const handleVariableHover = (variableName: string | null) => {
    if (variableName) {
      if (board.getVariable(variableName)?.enabled) {
        const panelIds = board.panelCollection.models
          .filter((p) => {
            const mapping = p.getVariableMapping(variableName);
            return mapping && mapping.disabled !== true;
          })
          .map((p) => p.id);
        $boards.setHighlightedPanelIds(panelIds);
      }
    } else {
      $boards.setHighlightedPanelIds([]);
    }
  };

  const handleRefresh = () => {
    if (panel) {
      panel.queryModel?.run();
    } else {
      board.refreshUdePanels();
    }
  };

  const handlePresetFiltersChange = (filters: Variable[]) => {
    board.set('config', { ...board.get('config'), variables: filters });
  };

  const { handlePresetFiltersChange: handleVariableChange } = useBoardVariableUrlSync(
    board.get('config').variables ?? [],
    handlePresetFiltersChange
  );

  useBoardTimeUrlSync(board.get('config').time, !panel);

  if (!hasMeasurement) {
    return null;
  }

  const content = (
    <Flex alignItems="flex-start" justifyContent="space-between" gap={1}>
      <Flex alignItems="flex-start" gap={1} flexWrap="wrap" minWidth={0} flex="1 1 0">
        {onOpenFilters && <Button icon={TbFilter} text="Filters" onClick={onOpenFilters} />}
        <BoardVariables
          board={board}
          onPresetFiltersChange={handleVariableChange}
          onVariableHover={handleVariableHover}
        />
      </Flex>

      <Flex gap={2} flexShrink={0}>
        {rightSlot}
        {panel?.queryModel && <UdeDebugButton queryModel={panel.queryModel} />}
        <TimeNavigationControl
          timeRange={timeRange}
          timezone={timezone}
          autoRefresh={autoRefresh}
          hasRunQuery={hasRunQuery}
          isLoading={isLoading}
          onTimeChange={onTimeChange}
          onTimezoneChange={onTimezoneChange}
          onLiveUpdateChange={onLiveUpdateChange}
          onRefresh={handleRefresh}
        />
      </Flex>
    </Flex>
  );

  if (sticky) {
    return <StickyWrapper $collapsed={collapsed}>{content}</StickyWrapper>;
  }

  return content;
}

export default observer(UdeToolBar);
